import { $fetch } from 'ofetch';

// Retry helper
async function retryFetch<T>(
  fn: () => Promise<T>,
  maxRetries = 3,
  delay = 1000,
  label = ''
): Promise<T> {
  let attempt = 0;
  while (attempt < maxRetries) {
    try {
      return await fn();
    } catch (err) {
      attempt++;
      if (attempt >= maxRetries) {
        console.error(
          `[Storyblok Retry] Failed after ${maxRetries} attempts${
            label ? ' for ' + label : ''
          }:`,
          err
        );
        throw err;
      }
      const wait = delay * Math.pow(2, attempt - 1);
      console.warn(
        `[Storyblok Retry] Attempt ${attempt} failed${
          label ? ' for ' + label : ''
        }, retrying in ${wait}ms...`,
        err
      );
      await new Promise((res) => setTimeout(res, wait));
    }
  }
  throw new Error('Unreachable');
}

/*
 * In-memory cache for Storyblok stories during a single build/run. Because Nuxt static generation can
 * render multiple pages concurrently (see `nitro.prerender.concurrency`), without this cache each page
 * would hit the Storyblok CDN individually, slowing down the build dramatically and wasting API quota.
 *
 * The data only needs to be fetched once per Node process, so we memoise the result and share an in-flight
 * promise with any concurrent callers.
 */

let _storiesCache: any[] | null = null;
let _storiesPromise: Promise<any[]> | null = null;

export const getStories = (): Promise<any[]> => {
  // Return cached result if available
  if (_storiesCache) {
    return Promise.resolve(_storiesCache);
  }

  // If a fetch is already in progress, return the same promise
  if (_storiesPromise) {
    return _storiesPromise;
  }

  const PER_PAGE = 100;
  const API = 'https://api-us.storyblok.com/v2/cdn';

  let page = 1;
  let stories: any[] = [];
  let loop = true;

  _storiesPromise = (async () => {
    const spaceVersion = await $fetch(`${API}/spaces/me`, {
      query: {
        token: process.env.STORYBLOK_API_KEY,
      },
    }).then((response) => response.space.version);

    while (loop) {
      await $fetch(`${API}/stories`, {
        query: {
          cv: spaceVersion,
          page,
          per_page: PER_PAGE,
          resolve_links: 'url',
          sort_by: 'position:asc',
          token: process.env.STORYBLOK_API_KEY,
          version: process.env.STORYBLOK_API_VERSION || 'draft',
        },
      })
        .then((response) => {
          stories = stories.concat(response.stories);
          if (response.stories.length === PER_PAGE) page++;
          else loop = false;
        })
        .catch((error) => {
          loop = false;
          // eslint-disable-next-line no-console
          console.error(error);
        });
    }

    _storiesCache = stories;
    return stories;
  })();

  return _storiesPromise;
};
